import React, {useState, useMemo, useEffect} from "react";
import {BrowserRouter as Router, Routes, Route} from "react-router-dom";
import DashboardLayout from "./Layout/DashboardLayout";
import {createStore} from "redux";
import AlertReducer from "./Redux/AlertReducer";
import {createTheme, ThemeProvider} from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline"; // Reset CSS for MUI
import LoginPage from "./Page/login";
import {ToastContainer} from "react-toastify";
import {ProtectedRoute} from "./routes/ProtectedRoute";
import {AuthProvider} from "./Providers/AuthContext";
import Unauthorized from "./Page/unauthorized";
import NotFound from "./Page/default/notfound";
import {ChatInterface} from "./Page/Chat/ChatInterface.tsx";

const store = createStore(AlertReducer);

function App() {
    const [themeMode, setThemeMode] = useState(
        localStorage.getItem("theme") === "light"
            ? "light"
            : localStorage.getItem("theme") === "dark"
            ? "dark"
            : "light"
    ); // State to manage theme mode

    // Define light and dark themes
    const theme = useMemo(
        () =>
            createTheme({
                palette: {
                    mode: themeMode,
                    primary: {
                        main: "#114787",
                        contrastText: "#fff",
                    },
                    secondary: {
                        main: "#f0f3f7",
                    },
                },
            }),
        [themeMode]
    );

    useEffect(() => {
        const darkModeEnabled = localStorage.getItem("theme") === "dark";
        if (darkModeEnabled) {
            document.documentElement.classList.add("dark");
        } else {
            document.documentElement.classList.remove("dark");
        }
    }, [themeMode]);

    // Toggle theme handler
    const toggleTheme = () => {
        setThemeMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
        localStorage.setItem("theme", themeMode === "light" ? "dark" : "light");
    };

    return (
        <div className="App">
            <ThemeProvider theme={theme}>
                <CssBaseline/>
                <ToastContainer/>
                <Router>
                    <AuthProvider>
                        <Routes>
                            <Route
                                path="/login"
                                element={<LoginPage thememode={themeMode}/>}
                            />
                            <Route path="/unauthorized" element={<Unauthorized/>}/>

                            {/* Protected Routes - Authentication and Permission Required */}
                            <Route
                                path="/Dashboard"
                                element={
                                    <ProtectedRoute requiredPermission="dashboard.stats">
                                        <DashboardLayout
                                            content="Dashboard"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/chats"
                                element={
                                    <ChatInterface />
                                }
                            />
                            <Route
                                path="/chats/:type/:id"
                                element={
                                    <ChatInterface />
                                }
                            />
                            <Route
                                path="/chats/:type/:user_type/:id"
                                element={
                                    <ChatInterface />
                                }
                            />
                            <Route
                                path="/chats/:type"
                                element={
                                    <ChatInterface />
                                }
                            />

                            {/* Protected Routes - Authentication and Permission Required */}
                            <Route
                                path="/email-builder"
                                element={
                                    <ProtectedRoute requiredPermission="dashboard.stats">
                                        <DashboardLayout
                                            content="EmailBuilder"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    </ProtectedRoute>
                                }
                            />

                            {/* Protected Routes - Authentication and Permission Required */}
                            <Route
                                path="/resellers-statistics"
                                element={
                                    <ProtectedRoute requiredPermission="orders.index">
                                        <DashboardLayout
                                            content="ResellersStatistics"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    </ProtectedRoute>
                                }
                            />

                            {/* Protected Routes - Authentication and Permission Required */}
                            <Route
                                path="/email-template"
                                element={
                                    <ProtectedRoute requiredPermission="dashboard.stats">
                                        <DashboardLayout
                                            content="EmailTemplate"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/email-template"
                                element={
                                    <ProtectedRoute requiredPermission="dashboard.stats">
                                        <DashboardLayout
                                            content="EmailTemplate"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    </ProtectedRoute>
                                }
                            />

                            {/* Protected Routes - Authentication and Permission Required */}
                            <Route
                                path="/ticket-builder"
                                element={
                                    <ProtectedRoute requiredPermission="dashboard.stats">
                                        <DashboardLayout
                                            content="TicketBuilder"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/Reviews/Panels"
                                element={
                                    <ProtectedRoute requiredPermission="panelsReviews.index">
                                        <DashboardLayout
                                            content="Reviews-Panels"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/Reviews/Services"
                                element={
                                    <ProtectedRoute requiredPermission="servicesreviews.index">
                                        <DashboardLayout
                                            content="Reviews-Services"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/Support"
                                element={
                                    <ProtectedRoute requiredPermission="supportTickets.index">
                                        <DashboardLayout
                                            content="Support"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/Support/:id"
                                element={
                                    <ProtectedRoute requiredPermission="supportTickets.messages">
                                        <DashboardLayout
                                            content="Support-Chat"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/TelegramGroups"
                                element={
                                    <ProtectedRoute requiredPermission="telegramGroups.index">
                                        <DashboardLayout
                                            content="TelegramGroups"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    </ProtectedRoute>
                                }
                            />

                            <Route
                                path="/AddTelegramGroups"
                                element={
                                    <ProtectedRoute requiredPermission="telegramGroups.store">
                                        <DashboardLayout
                                            content="AddTelegramGroups"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="TelegramGroups/Edit/:id"
                                element={
                                    <ProtectedRoute requiredPermission="telegramGroups.update">
                                        <DashboardLayout
                                            content="EditTelegramGroups"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/skypeGroups"
                                element={
                                    <ProtectedRoute requiredPermission="skypeGroups.index">
                                        <DashboardLayout
                                            content="SkypeGroups"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/AddSkypeGroups"
                                element={
                                    <ProtectedRoute requiredPermission="skypeGroups.store">
                                        <DashboardLayout
                                            content="AddSkypeGroups"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="SkypeGroups/Edit/:id"
                                element={
                                    <ProtectedRoute requiredPermission="skypeGroups.update">
                                        <DashboardLayout
                                            content="EditSkypeGroups"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/WhatsappGroups"
                                element={
                                    <ProtectedRoute requiredPermission="whatsappGroups.index">
                                        <DashboardLayout
                                            content="WhatsappGroups"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/AddWhatsappGroups"
                                element={
                                    <ProtectedRoute requiredPermission="whatsappGroups.store">
                                        <DashboardLayout
                                            content="AddWhatsappGroups"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="WhatsappGroups/Edit/:id"
                                element={
                                    <ProtectedRoute requiredPermission="whatsappGroups.update">
                                        <DashboardLayout
                                            content="EditWhatsappGroups"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/Shop-ADS"
                                element={
                                    <ProtectedRoute requiredPermission="ourServices.index">
                                        <DashboardLayout
                                            content="Shop-ADS"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/Add-AD"
                                element={
                                    <ProtectedRoute requiredPermission="ourServices.store">
                                        <DashboardLayout
                                            content="Add-AD"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/Edit-AD/:id"
                                element={
                                    <ProtectedRoute requiredPermission="ourServices.update">
                                        <DashboardLayout
                                            content="Edit-AD"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/Offers"
                                element={
                                    <ProtectedRoute requiredPermission="offers.index">
                                        <DashboardLayout
                                            content="Offers"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/Add-Offer"
                                element={
                                    <ProtectedRoute requiredPermission="offers.store">
                                        <DashboardLayout
                                            content="Add-Offer"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/Offers/Edit/:id"
                                element={
                                    <ProtectedRoute requiredPermission="offers.update">
                                        <DashboardLayout
                                            content="Edit-Offer"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/Ads-Pin"
                                element={
                                    <ProtectedRoute requiredPermission="pins.index">
                                        <DashboardLayout
                                            content="Ads-Pin"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/Ads-Pin/Edit/:id"
                                element={
                                    <ProtectedRoute requiredPermission="pins.update">
                                        <DashboardLayout
                                            content="Edit-Ads-Pin"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/Ads-Pin/Show/:id"
                                element={
                                    <ProtectedRoute requiredPermission="pins.update">
                                        <DashboardLayout
                                            content="Show-Ads-Pin"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/Ads-Main-Page"
                                element={
                                    <ProtectedRoute requiredPermission="banners.index">
                                        <DashboardLayout
                                            content="Ads-Main-Page"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/Ads-Main-Page/Edit/:id"
                                element={
                                    <ProtectedRoute requiredPermission="banners.update">
                                        <DashboardLayout
                                            content="Edit-Ads-Main-Page"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/Ads-Main-Page/Show/:id"
                                element={
                                    <ProtectedRoute requiredPermission="banners.index">
                                        <DashboardLayout
                                            content="Show-Ads-Main-Page"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/Ads-Search-Result"
                                element={
                                    <ProtectedRoute requiredPermission="topResults.index">
                                        <DashboardLayout
                                            content="Ads-Search-Result"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/Ads-Search-Result/Show/:id"
                                element={
                                    <ProtectedRoute requiredPermission="topResults.index">
                                        <DashboardLayout
                                            content="Show-Ads-Search-Result"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/Ads-Ticket"
                                element={
                                    <ProtectedRoute requiredPermission="ticketAds.index">
                                        <DashboardLayout
                                            content="Ads-Ticket"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/Ads-Ticket/Edit/:id"
                                element={
                                    <ProtectedRoute requiredPermission="ticketAds.update">
                                        <DashboardLayout
                                            content="Edit-Ads-Ticket"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/Trust"
                                element={
                                    <ProtectedRoute requiredPermission="trust.index">
                                        <DashboardLayout
                                            content="Trust"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/Trust/Edit/:id"
                                element={
                                    <ProtectedRoute requiredPermission="trust.update">
                                        <DashboardLayout
                                            content="Edit-Trust"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/Orders"
                                element={
                                    <ProtectedRoute requiredPermission="orders.index">
                                        <DashboardLayout
                                            content="Orders"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/Add-Order-By-Service"
                                element={
                                    <ProtectedRoute requiredPermission="orders.store">
                                        <DashboardLayout
                                            content="Add-Order-By-Service"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/Add-Order-By-Offer"
                                element={
                                    <ProtectedRoute requiredPermission="offerOrders.store">
                                        <DashboardLayout
                                            content="Add-Order-By-Offer"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/Add-Custom-Offer"
                                element={
                                    <ProtectedRoute requiredPermission="customOffer.store">
                                        <DashboardLayout
                                            content="Add-Custom-Offer"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/Orders/:id"
                                element={
                                    <ProtectedRoute requiredPermission="dashboard.stats">
                                        <DashboardLayout
                                            content="Order-Details"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/Orders-review/:id"
                                element={
                                    <ProtectedRoute requiredPermission="dashboard.stats">
                                        <DashboardLayout
                                            content="Order-Review"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    </ProtectedRoute>
                                }
                            />
                            <Route
                                path="/customoffer-review/:id"
                                element={
                                    <ProtectedRoute requiredPermission="dashboard.stats">
                                        <DashboardLayout
                                            content="CustomOffer-review"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    </ProtectedRoute>
                                }
                            />
                            <Route path="/Campaigns">
                                <Route
                                    index
                                    element={
                                        <ProtectedRoute requiredPermission="users.index">
                                            <DashboardLayout
                                                content="Campaigns"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path=":id"
                                    element={
                                        <ProtectedRoute requiredPermission="users.index">
                                            <DashboardLayout
                                                content="Campaign-Details"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="email/:id"
                                    element={
                                        <ProtectedRoute requiredPermission="users.index">
                                            <DashboardLayout
                                                content="Campaign-Email"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                            </Route>

                            <Route path="/UserCampaigns">
                                <Route
                                    index
                                    element={
                                        <ProtectedRoute requiredPermission="userCampaign.index">
                                            <DashboardLayout
                                                content="UserCampaigns"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path=":id"
                                    element={
                                        <ProtectedRoute requiredPermission="userCampaign.index">
                                            <DashboardLayout
                                                content="UserCampaigns-Details"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="email/:id"
                                    element={
                                        <ProtectedRoute requiredPermission="userCampaign.index">
                                            <DashboardLayout
                                                content="UserCampaigns-Email"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                            </Route>
                            <Route path="/Users">
                                <Route
                                    index
                                    element={
                                        <ProtectedRoute requiredPermission="users.index">
                                            <DashboardLayout
                                                content="Users"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="Add"
                                    element={
                                        <ProtectedRoute requiredPermission="users.store">
                                            <DashboardLayout
                                                content="Add-Users"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path=":id"
                                    element={
                                        <ProtectedRoute requiredPermission="users.show">
                                            <DashboardLayout
                                                content="User-Details"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="edit/:id"
                                    element={
                                        <ProtectedRoute requiredPermission="users.update">
                                            <DashboardLayout
                                                content="User-Edit"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                            </Route>

                            <Route path="/Membership">
                                <Route
                                    index
                                    element={
                                        <ProtectedRoute requiredPermission="memberships.index">
                                            <DashboardLayout
                                                content="Membership"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="Add"
                                    element={
                                        <ProtectedRoute requiredPermission="memberships.store">
                                            <DashboardLayout
                                                content="Add-Membership"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path=":id"
                                    element={
                                        <ProtectedRoute requiredPermission="memberships.show">
                                            <DashboardLayout
                                                content="Membership-Details"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="edit/:id"
                                    element={
                                        <ProtectedRoute requiredPermission="memberships.update">
                                            <DashboardLayout
                                                content="Membership-Edit"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                            </Route>

                            <Route path="/Membership-Services">
                                <Route
                                    index
                                    element={
                                        <ProtectedRoute requiredPermission="membershipServices.index">
                                            <DashboardLayout
                                                content="Membership-Services"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="Add"
                                    element={
                                        <ProtectedRoute requiredPermission="membershipServices.store">
                                            <DashboardLayout
                                                content="Add-Membership-Service"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path=":id"
                                    element={
                                        <ProtectedRoute requiredPermission="membershipServices.show">
                                            <DashboardLayout
                                                content="Membership-Service-Details"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="edit/:id"
                                    element={
                                        <ProtectedRoute requiredPermission="membershipServices.update">
                                            <DashboardLayout
                                                content="Membership-Service-Edit"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                            </Route>

                            <Route path="/Memberships-Subscriptions">
                                <Route
                                    index
                                    element={
                                        <ProtectedRoute requiredPermission="membershipServices.index">
                                            <DashboardLayout
                                                content="Memberships-Subscriptions"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="Add"
                                    element={
                                        <ProtectedRoute requiredPermission="membershipServices.store">
                                            <DashboardLayout
                                                content="Add-Membership-Service"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path=":id"
                                    element={
                                        <ProtectedRoute requiredPermission="membershipServices.show">
                                            <DashboardLayout
                                                content="Membership-Service-Details"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="edit/:id"
                                    element={
                                        <ProtectedRoute requiredPermission="membershipServices.update">
                                            <DashboardLayout
                                                content="Membership-Service-Edit"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                            </Route>


                            <Route path="/Membership-Groups">
                                <Route
                                    index
                                    element={
                                        <ProtectedRoute requiredPermission="membershipServices.index">
                                            <DashboardLayout
                                                content="Membership-Groups"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="Add"
                                    element={
                                        <ProtectedRoute requiredPermission="membershipServices.store">
                                            <DashboardLayout
                                                content="Add-Membership-Group"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path=":id"
                                    element={
                                        <ProtectedRoute requiredPermission="membershipServices.show">
                                            <DashboardLayout
                                                content="Membership-Group-Details"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="edit/:id"
                                    element={
                                        <ProtectedRoute requiredPermission="membershipServices.update">
                                            <DashboardLayout
                                                content="Membership-Group-Edit"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                            </Route>


                            <Route path="/Employees">
                                <Route
                                    index
                                    element={
                                        <ProtectedRoute requiredPermission="employees.index">
                                            <DashboardLayout
                                                content="Employees"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="Add"
                                    element={
                                        <ProtectedRoute requiredPermission="employees.store">
                                            <DashboardLayout
                                                content="Add-Employee"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="Edit/:id"
                                    element={
                                        <ProtectedRoute requiredPermission="employees.update">
                                            <DashboardLayout
                                                content="Edit-Employee"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                            </Route>


                            <Route path="/roles">
                                <Route
                                    index
                                    element={
                                        <ProtectedRoute requiredPermission="roles.index">
                                            <DashboardLayout
                                                content="roles"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="Add"
                                    element={
                                        <ProtectedRoute requiredPermission="roles.store">
                                            <DashboardLayout
                                                content="Add-roles"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="Edit/:id"
                                    element={
                                        <ProtectedRoute requiredPermission="roles.update">
                                            <DashboardLayout
                                                content="Edit-roles"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                            </Route>



                            <Route path="/UserAds">
                                <Route
                                    path="Ads"
                                    element={
                                        // <ProtectedRoute requiredPermission="employees.store">
                                        <DashboardLayout
                                            content="Ads"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                        // </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="Ads/:id"
                                    element={
                                        // <ProtectedRoute requiredPermission="employees.store">
                                        <DashboardLayout
                                            content="Show-Ad"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                        // </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="AdCategories"
                                    element={
                                        // <ProtectedRoute requiredPermission="employees.store">
                                        <DashboardLayout
                                            content="Ad-Categories"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                        // </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="AdCategories/Add"
                                    element={
                                        // <ProtectedRoute requiredPermission="employees.store">
                                        <DashboardLayout
                                            content="Ad-Categories-Add"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                        // </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="AdCategories/Edit/:id"
                                    element={
                                        // <ProtectedRoute requiredPermission="employees.store">
                                        <DashboardLayout
                                            content="Ad-Categories-Edit"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                        // </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="AdSubCategories"
                                    element={
                                        // <ProtectedRoute requiredPermission="employees.store">
                                        <DashboardLayout
                                            content="Ad-AdSubCategories"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                        // </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="AdSubCategories/Add"
                                    element={
                                        // <ProtectedRoute requiredPermission="employees.store">
                                        <DashboardLayout
                                            content="Ad-AdSubCategories-Add"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                        // </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="AdSubCategories/Edit/:id"
                                    element={
                                        // <ProtectedRoute requiredPermission="employees.store">
                                        <DashboardLayout
                                            content="Ad-AdSubCategories-Edit"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                        // </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="AdSubCategoryTypes"
                                    element={
                                        // <ProtectedRoute requiredPermission="employees.store">
                                        <DashboardLayout
                                            content="Ad-SubCategoryTypes"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                        // </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="AdSubCategoryTypes/Add"
                                    element={
                                        // <ProtectedRoute requiredPermission="employees.store">
                                        <DashboardLayout
                                            content="Ad-SubCategoryTypes-Add"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                        // </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="AdSubCategoryTypes/Edit/:id"
                                    element={
                                        // <ProtectedRoute requiredPermission="employees.store">
                                        <DashboardLayout
                                            content="Ad-SubCategoryTypes-Edit"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                        // </ProtectedRoute>
                                    }
                                />
                            </Route>

                            <Route path="/Panels">
                                <Route
                                    index
                                    element={
                                        <ProtectedRoute requiredPermission="panels.index">
                                            <DashboardLayout
                                                content="Panels"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="Add"
                                    element={
                                        <ProtectedRoute requiredPermission="panels.store">
                                            <DashboardLayout
                                                content="Add-Panel"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="Edit/:id"
                                    element={
                                        <ProtectedRoute requiredPermission="panels.update">
                                            <DashboardLayout
                                                content="Edit-Panel"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path=":id"
                                    element={
                                        <ProtectedRoute requiredPermission="panels.show">
                                            <DashboardLayout
                                                content="View-Panel"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="Log/:id"
                                    element={
                                        <ProtectedRoute requiredPermission="panels.show">
                                            <DashboardLayout
                                                content="Log-Panel"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="Trusted"
                                    element={
                                        <ProtectedRoute requiredPermission="panels.index">
                                            <DashboardLayout
                                                content="View-Trusted-Panel"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="Log"
                                    element={
                                        // <ProtectedRoute requiredPermission="panels.log'">
                                        <DashboardLayout
                                            content="View-Panels-Log"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                        // </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="UpdatedPanels"
                                    element={
                                        // <ProtectedRoute requiredPermission="panels.log'">
                                        <DashboardLayout
                                            content="View-UpdatedPanels"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                        // </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="PanelsByType"
                                    element={
                                        // <ProtectedRoute requiredPermission="panels.log'">
                                        <DashboardLayout
                                            content="View-PanelsByType"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                        // </ProtectedRoute>
                                    }
                                />
                            </Route>

                            <Route
                                path=""
                                element={
                                    <ProtectedRoute requiredPermission="dashboard.stats">
                                        <DashboardLayout
                                            content="Dashboard"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    </ProtectedRoute>
                                }
                            />
                            <Route path="/Resellers">
                                <Route
                                    index
                                    element={
                                        <ProtectedRoute requiredPermission="resellers.index">
                                            <DashboardLayout
                                                content="Resellers"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="Add"
                                    element={
                                        <ProtectedRoute requiredPermission="resellers.store">
                                            <DashboardLayout
                                                content="Add-Reseller"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="Edit"
                                    element={
                                        <ProtectedRoute requiredPermission="resellers.update">
                                            <DashboardLayout
                                                content="Edit-Reseller"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="Edit/:id"
                                    element={
                                        <ProtectedRoute requiredPermission="resellers.update">
                                            <DashboardLayout
                                                content="Edit-Reseller"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path=":id"
                                    element={
                                        <ProtectedRoute requiredPermission="resellers.show">
                                            <DashboardLayout
                                                content="View-Reseller"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                            </Route>
                            <Route path="/SubCategories">
                                <Route
                                    index
                                    element={
                                        <ProtectedRoute requiredPermission="subcategories.index">
                                            <DashboardLayout
                                                content="SubCategories"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="Add"
                                    element={
                                        <ProtectedRoute requiredPermission="subcategories.store">
                                            <DashboardLayout
                                                content="Add-SubCategory"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="Edit/:id"
                                    element={
                                        <ProtectedRoute requiredPermission="subcategories.update">
                                            <DashboardLayout
                                                content="Edit-SubCategory"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path=":id"
                                    element={
                                        <ProtectedRoute requiredPermission="subcategories.show">
                                            <DashboardLayout
                                                content="View-SubCategory"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                            </Route>

                            <Route path="/Sliders">
                                <Route
                                    index
                                    element={
                                        <ProtectedRoute requiredPermission="sliders.index">
                                            <DashboardLayout
                                                content="Sliders"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="Add"
                                    element={
                                        <ProtectedRoute requiredPermission="sliders.store">
                                            <DashboardLayout
                                                content="Add-Slider"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="Edit/:id"
                                    element={
                                        <ProtectedRoute requiredPermission="sliders.update">
                                            <DashboardLayout
                                                content="Edit-Slider"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path=":id"
                                    element={
                                        <ProtectedRoute requiredPermission="sliders.show">
                                            <DashboardLayout
                                                content="View-Slider"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                            </Route>

                            <Route path="/Categories">
                                <Route
                                    index
                                    element={
                                        <ProtectedRoute requiredPermission="categories.index">
                                            <DashboardLayout
                                                content="Categories"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="Add"
                                    element={
                                        <ProtectedRoute requiredPermission="categories.store">
                                            <DashboardLayout
                                                content="Add-Category"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="Edit/:id"
                                    element={
                                        <ProtectedRoute requiredPermission="categories.update">
                                            <DashboardLayout
                                                content="Edit-Category"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path=":id"
                                    element={
                                        <ProtectedRoute requiredPermission="categories.show">
                                            <DashboardLayout
                                                content="View-Category"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                            </Route>

                            <Route path="/SubCategories">
                                <Route
                                    index
                                    element={
                                        <ProtectedRoute requiredPermission="subcategories.index">
                                            <DashboardLayout
                                                content="SubCategories"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="Add"
                                    element={
                                        <ProtectedRoute requiredPermission="subcategories.store">
                                            <DashboardLayout
                                                content="Add-SubCategory"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="Edit/:id"
                                    element={
                                        <ProtectedRoute requiredPermission="subcategories.update">
                                            <DashboardLayout
                                                content="Edit-SubCategory"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path=":id"
                                    element={
                                        <ProtectedRoute requiredPermission="subcategories.show">
                                            <DashboardLayout
                                                content="View-SubCategory"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                            </Route>

                            <Route path="/Categories">
                                <Route
                                    index
                                    element={
                                        <ProtectedRoute requiredPermission="categories.index">
                                            <DashboardLayout
                                                content="Categories"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="Add"
                                    element={
                                        <ProtectedRoute requiredPermission="categories.store">
                                            <DashboardLayout
                                                content="Add-Categories"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="Edit/:id"
                                    element={
                                        <ProtectedRoute requiredPermission="categories.update">
                                            <DashboardLayout
                                                content="Edit-Categories"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path=":id"
                                    element={
                                        <ProtectedRoute requiredPermission="categories.show">
                                            <DashboardLayout
                                                content="View-Categories"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                            </Route>
                            <Route path="/Faqs">
                                <Route
                                    index
                                    element={
                                        <ProtectedRoute requiredPermission="faqs.index">
                                            <DashboardLayout
                                                content="Faqs"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="Add"
                                    element={
                                        <ProtectedRoute requiredPermission="faqs.store">
                                            <DashboardLayout
                                                content="Add-Faqs"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="Edit/:id"
                                    element={
                                        <ProtectedRoute requiredPermission="faqs.update">
                                            <DashboardLayout
                                                content="Edit-Faqs"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path=":id"
                                    element={
                                        <ProtectedRoute requiredPermission="faqs.show">
                                            <DashboardLayout
                                                content="View-Faqs"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                            </Route>

                            <Route path="/Marketers">
                                <Route
                                    index
                                    element={
                                        <ProtectedRoute requiredPermission="marketers.index">
                                            <DashboardLayout
                                                content="Marketers"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="Add"
                                    element={
                                        <ProtectedRoute requiredPermission="marketers.store">
                                            <DashboardLayout
                                                content="Add-Marketers"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="Edit/:id"
                                    element={
                                        <ProtectedRoute requiredPermission="marketers.update">
                                            <DashboardLayout
                                                content="Edit-Marketers"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path=":id"
                                    element={
                                        <ProtectedRoute requiredPermission="marketers.show">
                                            <DashboardLayout
                                                content="View-Marketers"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                            </Route>

                            <Route path="/Settings">
                                <Route
                                    index
                                    element={
                                        <DashboardLayout
                                            content="Settings"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    }
                                />
                                <Route
                                    path="PageEditor/:slug"
                                    element={
                                        <DashboardLayout
                                            content="Settings-PageEditor"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    }
                                />
                            </Route>
                            <Route path="/EmailProviders">
                                <Route
                                    index
                                    element={
                                        <DashboardLayout
                                            content="EmailProviders"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    }
                                />
                                <Route
                                    path="Edit/:id"
                                    element={
                                        <DashboardLayout
                                            content="EmailProviders-Edit"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    }
                                />
                                <Route
                                    path="Add"
                                    element={
                                        <DashboardLayout
                                            content="Add-EmailProvider"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    }
                                />
                            </Route>


                            <Route path="/Smm-ticket-profile">
                                <Route
                                    index
                                    element={
                                        <DashboardLayout
                                            content="Smm-ticket-profile"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    }
                                />
                                <Route
                                    path="Tickets/:id"
                                    element={
                                        <DashboardLayout
                                            content="Tickets"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    }
                                />
                            </Route>

                            {/*<Route path="/SmmDomains">*/}
                                <Route
                                    path="/SmmDomains"
                                    element={
                                        <DashboardLayout
                                            content="SmmDomains"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    }
                                />
                                <Route
                                    path="/SmmDomainRequests"
                                    element={
                                        <DashboardLayout
                                            content="SmmDomainRequests"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    }
                                />
                                <Route
                                    path="/Transaction"
                                    element={
                                        <DashboardLayout requiredPermission="transaction.index"
                                            content="Transaction"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    }
                                />
                                <Route
                                    path="/AddSmmDomains"
                                    element={
                                        <DashboardLayout
                                            content="AddSmmDomains"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    }
                                />
                                <Route
                                    path="EditSmmDomains/:id"
                                    element={
                                        <DashboardLayout
                                            content="EditSmmDomains"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    }
                                />
                                <Route
                                    path="SmmDomains-Show/:id"
                                    element={
                                        <DashboardLayout
                                            content="Show-SmmDomains"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    }
                                />
                            {/*</Route>*/}
                            <Route path="/Smm-ticket-campaings">
                                <Route
                                    index
                                    element={
                                        <DashboardLayout requiredPermission='smmticket.index'
                                            content="Smm-ticket-campaings"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    }
                                />
                            </Route>

                            <Route path="/Services">
                                <Route
                                    index
                                    element={
                                        <DashboardLayout
                                            content="Services"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    }
                                />
                            </Route>

                            <Route path="/OrdersManagement">
                                <Route
                                    index
                                    element={
                                        <DashboardLayout
                                            content="OrdersManagement"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    }
                                />
                            </Route>

                            <Route path="/PanelsManagement">
                                <Route
                                    index
                                    element={
                                        <DashboardLayout
                                            content="PanelsManagement"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    }
                                />
                            </Route>

                            <Route path="/PeoplesManagement">
                                <Route
                                    index
                                    element={
                                        <DashboardLayout
                                            content="PeoplesManagement"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    }
                                />
                            </Route>

                            <Route path="/UserAdsManagement">
                                <Route
                                    index
                                    element={
                                        <DashboardLayout
                                            content="UserAdsManagement"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    }
                                />
                            </Route>

                            <Route path="/SiteManagement">
                                <Route
                                    index
                                    element={
                                        <DashboardLayout
                                            content="SiteManagement"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    }
                                />
                            </Route>

                            <Route path="/SupportManagement">
                                <Route
                                    index
                                    element={
                                        <DashboardLayout
                                            content="SupportManagement"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    }
                                />
                            </Route>

                            <Route path="/Profile">
                                <Route
                                    index
                                    element={
                                        <DashboardLayout
                                            content="Profile"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    }
                                />
                            </Route>

                            <Route path="/Dashboards">
                                <Route
                                    index
                                    element={
                                        <DashboardLayout
                                            content="Dashboards"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    }
                                />
                            </Route>
                            <Route path="/Emails">
                                <Route
                                    index
                                    element={
                                        <DashboardLayout
                                            content="Emails"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    }
                                />
                            </Route>

                            <Route path="/DetailedDashboard">
                                <Route
                                    index
                                    element={
                                        <DashboardLayout
                                            content="DetailedDashboard"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    }
                                />
                            </Route>

                            <Route path="/LastMinutes">
                                <Route
                                    index
                                    element={
                                        <DashboardLayout
                                            content="LastMinutes"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    }
                                />
                            </Route>

                            <Route path="/EmailTemplates">
                                <Route
                                    index
                                    element={
                                        <DashboardLayout
                                            content="EmailTemplates"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    }
                                />
                            </Route>

                            <Route path="/EmailTemplates/Add">
                                <Route
                                    index
                                    element={
                                        <DashboardLayout
                                            content="AddEmailTemplates"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    }
                                />
                            </Route>

                            <Route
                                path="EditEmailTemplates/:id"
                                element={
                                    <DashboardLayout
                                        content="EditEmailTemplates"
                                        toggleTheme={toggleTheme}
                                        thememode={themeMode}
                                    />
                                }
                            />

                            <Route path="/Deposits">
                                <Route
                                    index
                                    element={
                                        <DashboardLayout
                                            content="Deposits"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    }
                                />
                            </Route>

                            <Route path="/UpdatedOrders">
                                <Route
                                    index
                                    element={
                                        <DashboardLayout
                                            content="UpdatedOrders"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    }
                                />
                            </Route>

                            <Route path="/Notification">
                                <Route
                                    index
                                    element={
                                        <DashboardLayout
                                            content="Notification"
                                            toggleTheme={toggleTheme}
                                            thememode={themeMode}
                                        />
                                    }
                                />
                            </Route>
                            <Route path="/AddBalanceRequests">
                                <Route
                                    index
                                    element={
                                        <ProtectedRoute requiredPermission="addBalanceRequests.index">
                                            <DashboardLayout
                                                content="AddBalanceRequests"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="Add"
                                    element={
                                        <ProtectedRoute requiredPermission="addBalanceRequests.store">
                                            <DashboardLayout
                                                content="Add-AddBalanceRequest"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="Edit/:id"
                                    element={
                                        <ProtectedRoute requiredPermission="addBalanceRequests.update">
                                            <DashboardLayout
                                                content="Edit-AddBalanceRequest"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path=":id"
                                    element={
                                        <ProtectedRoute requiredPermission="addBalanceRequests.show">
                                            <DashboardLayout
                                                content="View-AddBalanceRequest"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                            </Route>
                            <Route path="/OrderRequests">
                                <Route
                                    index
                                    element={
                                        <ProtectedRoute requiredPermission="orderRequests.index">
                                            <DashboardLayout
                                                content="OrderRequests"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="Add"
                                    element={
                                        <ProtectedRoute requiredPermission="orderRequests.store">
                                            <DashboardLayout
                                                content="Add-OrderRequests"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="Edit/:id"
                                    element={
                                        <ProtectedRoute requiredPermission="orderRequests.update">
                                            <DashboardLayout
                                                content="Edit-OrderRequests"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path=":id"
                                    element={
                                        <ProtectedRoute requiredPermission="orderRequests.show">
                                            <DashboardLayout
                                                content="View-OrderRequests"
                                                toggleTheme={toggleTheme}
                                                thememode={themeMode}
                                            />
                                        </ProtectedRoute>
                                    }
                                />
                            </Route>
                            <Route path="*" element={<NotFound/>}/>
                        </Routes>
                    </AuthProvider>
                </Router>
            </ThemeProvider>
        </div>
    );
}

export default App;
